"use client";

import type { UseOpenApiInfiniteQueryOptions } from "@packages/sdk";
import {
  getRequestNextPageParam,
  keyCommunitiesAdminUsers,
  useOpenApiInfiniteQuery,
} from "@packages/sdk";

const urlBackendPath = "/communities/{community_id}/admin/users" as const;

/**
 * API version this query asks for, overriding the app-wide
 * `X-Hallow-Version` (2425, set in `next.config.js`).
 *
 * `joined_at` and `membership_length` are only on the member response from
 * 2460 (hallow-inc/backend#7264), and `sort_by=joined_at|membership_length`
 * is only accepted from the same version. The app-wide value cannot simply be
 * raised to 2460: it would cross five unrelated response-shape gates, so the
 * members table asks for the newer version on its own request instead, via
 * `withMembershipFields`.
 */
const X_HALLOW_VERSION_MEMBERS = "2460";

export const useRequestCommunitiesAdminUsers = ({
  params,
  withMembershipFields = false,
  ...props
}: Partial<UseOpenApiInfiniteQueryOptions<typeof urlBackendPath>> & {
  /**
   * Ask for {@link X_HALLOW_VERSION_MEMBERS} instead of the app-wide version,
   * so `joined_at` and `membership_length` come back on each member and
   * `sort_by` accepts them. Opt in only where those fields are actually read -
   * this hook also backs the roster, import status, member search, class
   * member and library pickers, and they stay on the app-wide version.
   */
  withMembershipFields?: boolean;
}) => {
  return useOpenApiInfiniteQuery({
    ...props,
    ...(withMembershipFields && {
      headers: {
        "X-Hallow-Version": X_HALLOW_VERSION_MEMBERS,
      },
    }),
    getNextPageParam: (lastPage, _allPages, lastPageParam: number) =>
      getRequestNextPageParam({
        total: lastPage.total,
        lastPageParam,
        pageSize: 50,
      }),
    initialPageParam: 0,
    params,
    queryKey: keyCommunitiesAdminUsers({
      id: params?.path?.community_id,
      ...params?.query,
    }),
    urlBackendPath,
  });
};
